/**
 * 班次依出發日分組 — `/schedule`（單一商品）與 `/plan`（套票／多商品行程）共用。
 *
 * 抽出來的原因很具體：`/plan` 少了日期標頭時，畫面上會出現二十個一模一樣的
 * 「10:00 - 11:00」按鈕（同一時段、不同天），顧客沒有任何辦法分辨自己按的是哪天。
 * 兩頁各寫一份的話，只有其中一頁會記得補。
 */

import type { ScheduleSummary } from './api';
import type { Locale } from './i18n';

/** 本地日期鍵（YYYY-MM-DD）——用 UTC 切會讓深夜班次被歸到隔天。 */
export function dayKeyOf(iso: string): string {
  const d = new Date(iso);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** 依出發日分組；輸入已由後端依時間遞增排序，故各組內也是遞增。 */
export function groupByDay(items: ScheduleSummary[]): [string, ScheduleSummary[]][] {
  const map = new Map<string, ScheduleSummary[]>();
  for (const s of items) {
    const k = dayKeyOf(s.departure_at);
    const arr = map.get(k);
    if (arr === undefined) map.set(k, [s]);
    else arr.push(s);
  }
  return [...map.entries()];
}

/** 日期標題：今日／明日以字詞表示，其餘給日期與星期。 */
export function dayLabel(dayKey: string, locale: Locale, t: (k: string) => string): string {
  const today = dayKeyOf(new Date().toISOString());
  if (dayKey === today) return t('schedule.today');
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (dayKey === dayKeyOf(tomorrow.toISOString())) return t('schedule.tomorrow');

  const [y, m, d] = dayKey.split('-').map(Number);
  const tag = locale === 'en' ? 'en-US' : locale === 'ja' ? 'ja-JP' : 'zh-TW';
  return new Date(y as number, (m as number) - 1, d as number).toLocaleDateString(tag, {
    month: 'numeric',
    day: 'numeric',
    weekday: 'short',
  });
}
